import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowUpRight } from "lucide-react"
import { useTeam } from "@/hooks/useTeam"
import { SectionSkeleton, ErrorState } from "@/components/state"
import { LinkedInIcon, TwitterIcon, GitHubIcon } from "@/components/social-icons"
import { pageMeta } from "@/lib/seo"

export const Route = createFileRoute("/_public/team")({
  component: Team,
  head: () =>
    pageMeta({
      title: "Team — Nexovia IT",
      description: "Meet the engineers, designers and strategists at Nexovia IT.",
      path: "/team",
    }),
})

const socialIcons = { linkedin: LinkedInIcon, twitter: TwitterIcon, github: GitHubIcon } as const

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("")
}

function Team() {
  const { data: team, isLoading, isError } = useTeam()
  const activeTeam = (team ?? []).filter((m) => m.is_active)

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-background">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{ background: "radial-gradient(ellipse at top, color-mix(in oklab, var(--rust-glow) 22%, transparent), transparent 60%)" }}
        />
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-28 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-rust">
            <span className="h-1.5 w-1.5 rounded-full bg-rust" /> Team
          </span>
          <h1 className="mx-auto mt-6 max-w-4xl text-5xl md:text-7xl font-bold text-balance">
            The People Building <span className="text-rust">Nexovia IT.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            Engineers, designers and strategists working across time zones to ship software that holds up under
            real-world scale.
          </p>
        </div>
      </section>

      {/* TEAM GRID */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
        {isLoading ? (
          <SectionSkeleton rows={8} className="md:grid-cols-3 lg:grid-cols-4" />
        ) : isError ? (
          <ErrorState message="Couldn't load the team right now." />
        ) : activeTeam.length === 0 ? (
          <p className="text-center text-muted-foreground">No team members published yet.</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-4">
            {activeTeam.map((m) => {
              const socials = (Object.keys(socialIcons) as (keyof typeof socialIcons)[]).filter(
                (k) => m.social_links?.[k],
              )
              return (
                <div
                  key={m.id}
                  className="group overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-card"
                >
                  <div className="relative aspect-square overflow-hidden bg-gradient-hero">
                    {m.photo_url ? (
                      <img
                        src={m.photo_url}
                        alt={m.name}
                        width={400}
                        height={400}
                        className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <span className="font-display text-4xl text-cream/90">{initials(m.name)}</span>
                      </div>
                    )}
                    {socials.length > 0 && (
                      <div className="absolute inset-x-0 bottom-0 flex justify-center gap-2 bg-gradient-to-t from-ink/80 to-transparent p-4 opacity-0 transition group-hover:opacity-100">
                        {socials.map((key) => {
                          const Icon = socialIcons[key]
                          return (
                            <a
                              key={key}
                              href={m.social_links[key] ?? "#"}
                              target="_blank"
                              rel="noreferrer"
                              className="flex h-8 w-8 items-center justify-center rounded-full bg-cream text-ink transition hover:bg-rust hover:text-cream"
                              aria-label={key}
                            >
                              <Icon className="h-4 w-4" />
                            </a>
                          )
                        })}
                      </div>
                    )}
                  </div>
                  <div className="p-5 text-center">
                    <p className="font-semibold">{m.name}</p>
                    <p className="mt-1 text-xs text-rust">{m.designation}</p>
                    {m.bio && <p className="mt-3 text-xs text-muted-foreground leading-relaxed">{m.bio}</p>}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-secondary/30">
        <div className="mx-auto max-w-5xl px-6 py-24 md:py-28 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-rust">Join us</p>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold">We're Always Looking for Great People.</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Don't see an open role that fits? Reach out anyway — we're always happy to talk to good engineers and
            designers.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
              Get in touch <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
